import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Receipt, ShieldCheck, FileText } from "lucide-react";
import { toast } from "sonner";

export default function GstSettings() {
  const [s, setS] = useState({
    legal_name: "", trade_name: "", gstin: "", pan: "",
    address_line1: "", address_line2: "", city: "", state: "", state_code: "", pincode: "",
    email: "", phone: "",
    bank_account_no: "", bank_ifsc: "", bank_name: "", bank_branch: "",
    invoice_prefix: "CARE", gst_rate: 18, pricing_mode: "inclusive",
    signatory_name: "", invoice_terms: "",
  });
  const [stateCodes, setStateCodes] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/gst/supplier-settings").then((r) => {
      setStateCodes(r.data.state_codes || {});
      if (r.data.settings && Object.keys(r.data.settings).length) {
        setS((prev) => ({ ...prev, ...r.data.settings }));
      }
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  // Auto-derive state from GSTIN
  const handleGstin = (v) => {
    const next = { ...s, gstin: v.toUpperCase() };
    if (v && v.length >= 2) {
      const sc = v.substring(0, 2);
      if (stateCodes[sc]) {
        next.state_code = sc;
        next.state = stateCodes[sc];
      }
    }
    setS(next);
  };

  const save = async () => {
    if (!s.legal_name || !s.gstin || !s.state_code) {
      toast.error("Legal name, GSTIN and state code are required.");
      return;
    }
    if (!/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z][Z][0-9A-Z]$/.test(s.gstin)) {
      toast.error("Invalid GSTIN format. It must be 15 characters as per CBIC norms.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/gst/supplier-settings", s);
      toast.success("GST settings saved. New subscription payments will auto-generate tax invoices.");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!loaded) return <Card className="p-6 text-sm text-ink-muted">Loading…</Card>;

  return (
    <div className="space-y-6" data-testid="gst-settings-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold flex items-center gap-2"><Receipt className="text-brand" />GST settings</h1>
        <p className="mt-1 text-sm text-ink-muted">
          Configure your CareO supplier identity. These details are <b>snapshotted onto every tax invoice</b> at the moment of issue —
          changes here will only affect future invoices.
        </p>
      </div>

      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-2 text-brand font-heading"><ShieldCheck size={18} />Supplier identity (CareO Pvt Ltd)</div>
        <div className="grid gap-4 md:grid-cols-2">
          <div><Label>Legal name *</Label><Input value={s.legal_name} onChange={e => setS({ ...s, legal_name: e.target.value })} data-testid="gst-legal-name" /></div>
          <div><Label>Trade / brand name</Label><Input value={s.trade_name || ""} onChange={e => setS({ ...s, trade_name: e.target.value })} placeholder="e.g. CareO" /></div>
          <div>
            <Label>GSTIN * <span className="ml-2 text-[10px] text-ink-muted">(15-char, auto-fills state)</span></Label>
            <Input value={s.gstin} onChange={e => handleGstin(e.target.value)} placeholder="29AABCC1234D1Z5" maxLength={15} className="font-mono uppercase" data-testid="gst-gstin" />
          </div>
          <div><Label>PAN</Label><Input value={s.pan || ""} onChange={e => setS({ ...s, pan: e.target.value.toUpperCase() })} className="font-mono uppercase" maxLength={10} /></div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div><Label>Address line 1 *</Label><Input value={s.address_line1} onChange={e => setS({ ...s, address_line1: e.target.value })} /></div>
          <div><Label>Address line 2</Label><Input value={s.address_line2 || ""} onChange={e => setS({ ...s, address_line2: e.target.value })} /></div>
          <div><Label>City *</Label><Input value={s.city} onChange={e => setS({ ...s, city: e.target.value })} /></div>
          <div>
            <Label>State *</Label>
            <Select value={s.state_code || "_none"} onValueChange={(v) => v === "_none" ? null : setS({ ...s, state_code: v, state: stateCodes[v] || "" })}>
              <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {Object.entries(stateCodes).map(([code, name]) => (
                  <SelectItem key={code} value={code}>{code} — {name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>PIN code</Label><Input value={s.pincode || ""} onChange={e => setS({ ...s, pincode: e.target.value })} maxLength={6} /></div>
          <div><Label>Billing email</Label><Input value={s.email || ""} onChange={e => setS({ ...s, email: e.target.value })} placeholder="info@stewardindia.com" /></div>
          <div><Label>Phone</Label><Input value={s.phone || ""} onChange={e => setS({ ...s, phone: e.target.value })} /></div>
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-2 text-brand font-heading"><Receipt size={18} />Invoice configuration</div>
        <div className="grid gap-4 md:grid-cols-3">
          <div><Label>Invoice prefix</Label><Input value={s.invoice_prefix} onChange={e => setS({ ...s, invoice_prefix: e.target.value.toUpperCase() })} placeholder="CARE" /><div className="mt-1 text-[10px] text-ink-muted">Format: <code>{s.invoice_prefix}/2026-27/00001</code></div></div>
          <div><Label>GST rate (%)</Label><Input type="number" min="0" max="28" step="0.1" value={s.gst_rate} onChange={e => setS({ ...s, gst_rate: Number(e.target.value) })} /><div className="mt-1 text-[10px] text-ink-muted">Standard SaaS rate is 18%</div></div>
          <div>
            <Label>Pricing mode</Label>
            <Select value={s.pricing_mode} onValueChange={(v) => setS({ ...s, pricing_mode: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inclusive">GST inclusive (plan price already contains GST)</SelectItem>
                <SelectItem value="exclusive">GST exclusive (GST added on top of plan price)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Authorised signatory name</Label>
          <Input value={s.signatory_name || ""} onChange={e => setS({ ...s, signatory_name: e.target.value })} placeholder="e.g. Director / Finance Head" />
        </div>
        <div>
          <Label>Terms & conditions (printed on every invoice)</Label>
          <textarea
            rows={4} value={s.invoice_terms || ""}
            onChange={e => setS({ ...s, invoice_terms: e.target.value })}
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
      </Card>

      <Card className="p-6 space-y-5">
        <div className="flex items-center gap-2 text-brand font-heading"><FileText size={18} />Bank details (printed on invoice for NEFT / RTGS)</div>
        <div className="grid gap-4 md:grid-cols-2">
          <div><Label>Bank name</Label><Input value={s.bank_name || ""} onChange={e => setS({ ...s, bank_name: e.target.value })} /></div>
          <div><Label>Branch</Label><Input value={s.bank_branch || ""} onChange={e => setS({ ...s, bank_branch: e.target.value })} /></div>
          <div><Label>Account number</Label><Input value={s.bank_account_no || ""} onChange={e => setS({ ...s, bank_account_no: e.target.value })} className="font-mono" /></div>
          <div><Label>IFSC code</Label><Input value={s.bank_ifsc || ""} onChange={e => setS({ ...s, bank_ifsc: e.target.value.toUpperCase() })} className="font-mono uppercase" /></div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-brand hover:bg-brand-700" data-testid="gst-save-btn">
          <Save size={14} className="mr-2" />{saving ? "Saving…" : "Save GST settings"}
        </Button>
      </div>
    </div>
  );
}
